import { EDesignerEntityKind, IConfig } from "@kuika/kuika-cl-model";
import * as React from "react";
import {
  hasRecentlyModified,
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  resolveActiveTab,
  selectRecentlyModified
} from "../../../shared/utility/recently-modified";
import { buildLastModifiedColumn } from "../../../shared/utility/recently-modified-columns";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import {
  configTestName,
  CONFIG_SCOPE_COLOUR,
  ConfigScope,
  countConfigPlatforms,
  filterConfigs,
  getConfigPlatforms,
  IConfigPlatform,
  isGlobalRow,
  isReadOnlyReference,
  nameOf
} from "./config-manager-model";
import { ConfigNameDialog, ConfigNameDialogState } from "./config-name-dialog";
import { IConfigRevealRequest, subscribeConfigReveal, takeConfigReveal } from "./config-reveal";
import { buildConfigRowActions } from "./config-row-actions";
import "./config-manager.scss";

/**
 * The Config Manager's landing page: the configurations you already have, as a table.
 *
 * ONE component serves both scopes. A configuration is the same entity either way — the
 * project's Config Manager and the workspace's Global Configurations differ only in which
 * store they read and whether the Project/Global split exists at all — and the two surfaces
 * already share every form body below this page (`AppSettings`, `WebConfig`, `IOSConfig`,
 * `AndroidConfig`). Sharing the landing is what keeps them from drifting the way their two
 * left panels did.
 *
 * It replaces both of those panels, so everything they could do lives here: the list, its
 * Project/Global split, the search, duplicate, delete, rename, "create a config from this
 * global config", both empty states, and the rule that a global row is a read-only
 * reference inside a project's list. Switching configurations WITHOUT leaving the editor is
 * the one thing a table cannot do; that is the editor header's own switcher.
 *
 * `IConfig` carries neither a timestamp nor an author, so when a configuration was last written,
 * and by whom, comes from the shared entity-modification feed — which is what the "Recently
 * modified" tab both surfaces open on, and the last two columns, both read.
 *
 * The create, "create from global" and rename dialogs are the SAME sheet (`ConfigNameModal`)
 * in three modes, tracked as one discriminated `nameDialog` rather than three booleans plus
 * a "which row" field — the three used to be reachable in combinations that made no sense
 * (a pending global row with the plain "create" title, say), which a union simply cannot be.
 *
 * Presentation only — every mutation belongs to the host, which owns the store (SD context
 * for the project, redux for the workspace) and the unsaved-changes handshake.
 */

const configRowTestId = (config: IConfig): string => `${configTestName(config)}Config`;

const PROJECT_TAB: ConfigScope = "project";
const GLOBAL_TAB: ConfigScope = "global";
/**
 * The workspace surface's one scope tab. Its value stays `global` — the store every row there
 * belongs to — but it is named for what it holds: every configuration the workspace has.
 */
const ALL_TAB_LABEL: string = "All";

/** The scope tabs each surface offers, which between them hold every row it lists. */
const PROJECT_SURFACE_SCOPE_TABS: ConfigScope[] = [PROJECT_TAB, GLOBAL_TAB];
const GLOBAL_SURFACE_SCOPE_TABS: ConfigScope[] = [GLOBAL_TAB];
/** Each surface's tabs in their on-screen order, so a switch slides the list the way the selection moved. */
const PROJECT_SURFACE_TAB_ORDER: string[] = [RECENTLY_MODIFIED_TAB, ...PROJECT_SURFACE_SCOPE_TABS];
const GLOBAL_SURFACE_TAB_ORDER: string[] = [RECENTLY_MODIFIED_TAB, ...GLOBAL_SURFACE_SCOPE_TABS];

/** A project's list merges the workspace's configurations in, so its feed asks about both tables. */
const PROJECT_SURFACE_FEED_KINDS: EDesignerEntityKind[] = [
  EDesignerEntityKind.ProjectConfiguration,
  EDesignerEntityKind.DomainConfiguration
];
const GLOBAL_SURFACE_FEED_KINDS: EDesignerEntityKind[] = [EDesignerEntityKind.DomainConfiguration];

export interface IConfigManagerLandingProps {
  /**
   * Which surface this is. `global` has one store, so it has no Project/Global split — its tabs are
   * "Recently modified" and "All".
   */
  scope: ConfigScope;
  /**
   * The project the page is open in, for the "Recently modified" feed. The project surface's host
   * passes it; the workspace surface has no project and leaves it out, and asks only for domain
   * configurations, which the feed answers without one.
   */
  projectId?: string;
  /** Every configuration the surface knows about; the project's list includes global rows. */
  configs: IConfig[];
  /** Id of the row whose duplicate or delete is in flight; its actions trigger spins. */
  busyConfigId?: string;
  /** Seeds the search box — the project surface restores its persisted query with it. */
  initialSearchText?: string;
  onSearchTextChange?: (searchText: string) => void;
  onOpenConfig: (config: IConfig) => void;
  /** `fromGlobal` is set when the new configuration inherits a global one's settings. */
  onCreateConfig: (name: string, fromGlobal?: IConfig) => void;
  /** Absent on the workspace surface, which never had a working duplicate. */
  onDuplicateConfig?: (config: IConfig) => void;
  /** The host owns the confirmation, so it survives the row disappearing under it. */
  onRequestDelete: (config: IConfig) => void;
  /**
   * Renames a row without opening its editor. Absent hides the row action entirely — a host
   * with nowhere to send the write should not offer it, the same rule `onDuplicateConfig`
   * follows. Resolves `true` once stored and `false` when the host declined the write (the
   * stale-write prompt's Cancel), which closes the sheet without a ✓. Rejecting leaves the dialog
   * open so the user can retry; the host is expected to have already reported the failure
   * (`KMainFunctions.exceptionHandler` and friends).
   */
  onRenameConfig?: (config: IConfig, name: string) => Promise<boolean>;
  /** Right-aligned header controls — the project surface's Project Settings entry. */
  headerActions?: React.ReactNode;
  /**
   * The list is being (re)fetched. Handed to the table as is: with no rows yet it draws its
   * skeleton rows, and with rows on screen it keeps them under its own refresh overlay — so no
   * "only while empty" condition belongs here.
   */
  isConfigListLoading?: boolean;
}

interface IConfigManagerLandingState {
  /**
   * The scope tab the page falls back to when it has nothing recent to open on. Fixed at
   * construction from `scope` and never set again: once the user picks a tab, `pickedTab` decides.
   */
  listType: ConfigScope;
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
  searchText: string;
  nameDialog: ConfigNameDialogState;
  /**
   * The configuration a closing editor saved, to light up once its row is in the list — the
   * editor's ✓ has already said "Saved", and this shows where the configuration went. Null when
   * nothing was asked for, or once the row has been shown.
   */
  revealConfigName: string | null;
}

export class ConfigManagerLanding extends React.PureComponent<IConfigManagerLandingProps, IConfigManagerLandingState> {
  /** Ends the subscription that hands this page a save made while it is already on screen. */
  private unsubscribeReveal: (() => void) | undefined = undefined;
  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: IConfigManagerLandingProps) {
    super(props);
    // The editor that just saved is what unmounted for this page to mount, so its request is
    // waiting. One left by the other surface names a row that is not in this list.
    const reveal: IConfigRevealRequest | null = takeConfigReveal();
    this.state = {
      // The workspace surface has only its own configurations; forcing `project` there
      // would filter every row out.
      listType: props.scope === "global" ? GLOBAL_TAB : PROJECT_TAB,
      pickedTab: null,
      feed: LOADING_FEED,
      searchText: props.initialSearchText ?? "",
      nameDialog: { kind: "closed" },
      revealConfigName: reveal !== null && reveal.scope === props.scope ? reveal.name : null
    };
  }

  componentDidMount(): void {
    this.isMounted_ = true;
    this.unsubscribeReveal = subscribeConfigReveal(this.handleRevealRequest);
    void this.loadFeed();
  }

  componentWillUnmount(): void {
    this.isMounted_ = false;
    this.unsubscribeReveal?.();
  }

  /**
   * The host swaps `configs` for a new array on every create, duplicate, and delete, and this page
   * stays mounted through all three (a duplicate never leaves it). The id set is the smallest
   * correct signal: it fires on those and stays quiet on an in-place rename, which this page reads
   * off the row itself rather than off the feed.
   */
  componentDidUpdate(prevProps: Readonly<IConfigManagerLandingProps>): void {
    if (haveItemIdsChanged(prevProps.configs ?? [], this.props.configs ?? [], (row: IConfig) => row.id)) {
      void this.loadFeed();
    }
  }

  /**
   * The project surface asks about both tables its list merges; the workspace has no project to
   * name, and its one kind is workspace-level, which the endpoint answers without one.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const isProject: boolean = this.isProjectSurface();
    const kinds: EDesignerEntityKind[] = isProject ? PROJECT_SURFACE_FEED_KINDS : GLOBAL_SURFACE_FEED_KINDS;
    const projectId: string | undefined = isProject ? this.props.projectId : undefined;
    const feed: IModificationFeed = await loadModificationFeed(kinds, projectId);
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  handleRevealRequest = (request: IConfigRevealRequest): void => {
    if (request.scope !== this.props.scope) return;
    this.setState({ revealConfigName: request.name });
  };

  isProjectSurface = (): boolean => this.props.scope === "project";

  handleSearchChange = (searchText: string): void => {
    // A reveal still waiting for its row was asked about the list as it stood; once the reader
    // narrows it themselves, lighting a row later would answer a question nobody is asking.
    this.setState({ searchText, revealConfigName: null });
    this.props.onSearchTextChange?.(searchText);
  };

  handleTabChange = (value: string): void => {
    this.setState({ pickedTab: value, revealConfigName: null });
  };

  getScopeTabs = (): ConfigScope[] =>
    this.isProjectSurface() ? PROJECT_SURFACE_SCOPE_TABS : GLOBAL_SURFACE_SCOPE_TABS;

  /**
   * Every row the surface's scope tabs list between them — both stores on a project, the one on
   * the workspace. The recent tab draws from this rather than from `props.configs`, so it never
   * holds a row no other tab would, such as the hidden configuration the Run button writes to.
   */
  getSurfaceConfigs = (searchText: string): IConfig[] =>
    this.getScopeTabs().flatMap((listType: ConfigScope): IConfig[] =>
      filterConfigs(this.props.configs ?? [], this.props.scope, listType, searchText)
    );

  /**
   * Whether the list the tab is chosen from has arrived. The project surface fetches its list
   * after this page mounts, so the feed can answer first; deciding then would read "nothing is
   * recent" off an empty list, drop to Project, and jump back once the rows land. A refetch with
   * rows already on screen is not that, and treating it as one would bounce the tab on every
   * duplicate and delete.
   */
  isListReady = (): boolean => this.props.isConfigListLoading !== true || (this.props.configs?.length ?? 0) > 0;

  /** The page is still choosing its own tab: nobody has picked one, and what it chooses from is on its way. */
  isChoosingTab = (): boolean =>
    this.state.pickedTab === null && (this.state.feed.status === "loading" || !this.isListReady());

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to the surface's full list when nothing is recent or the feed failed, and
   * otherwise shows whatever the user picked.
   */
  getActiveTab = (): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      // Asked of every configuration, not the searched ones: a query that matches nothing recent
      // must not move the user to another tab mid-keystroke.
      hasRecent: hasRecentlyModified<IConfig>(
        this.getSurfaceConfigs(""),
        this.state.feed.index,
        (row: IConfig) => row.id,
        Date.now()
      ),
      itemsReady: this.isListReady(),
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: this.state.listType
    });

  /**
   * The id of the row the closing editor saved, among the rows this tab shows. Null until the
   * refreshed list carries it, which is what keeps the reveal armed across the refetch — a create
   * is not in the list the page mounted with. Only rows this surface can write are candidates: a
   * read-only global reference never came out of this page's editor.
   */
  findRevealRowId = (rows: IConfig[]): string | null => {
    const name: string | null = this.state.revealConfigName;
    if (name === null) return null;
    const row: IConfig | undefined = rows.find(
      (candidate: IConfig): boolean => nameOf(candidate) === name && !isReadOnlyReference(this.props.scope, candidate)
    );
    return row?.id ?? null;
  };

  handleRowRevealed = (): void => {
    this.setState({ revealConfigName: null });
  };

  /**
   * The rows one pane lists: a scope tab its store's, and the recent tab whatever the feed dates
   * inside the window across all of them, newest first.
   */
  getPaneRows = (paneKey: string, searchText: string): IConfig[] => {
    if (paneKey !== RECENTLY_MODIFIED_TAB) {
      // Every other pane is a scope tab, and a scope tab's value is its scope.
      return filterConfigs(this.props.configs ?? [], this.props.scope, paneKey as ConfigScope, searchText);
    }
    return selectRecentlyModified<IConfig>({
      items: this.getSurfaceConfigs(searchText),
      index: this.state.feed.index,
      getId: (row: IConfig) => row.id,
      getName: nameOf,
      now: Date.now()
    });
  };

  openCreateModal = (): void => this.setState({ nameDialog: { kind: "create" } });

  openCreateFromGlobalModal = (source: IConfig): void =>
    this.setState({ nameDialog: { kind: "createFromGlobal", source } });

  openRenameModal = (row: IConfig): void => this.setState({ nameDialog: { kind: "rename", row } });

  closeNameModal = (): void => this.setState({ nameDialog: { kind: "closed" } });

  /**
   * A global row inside a PROJECT's list is a read-only reference — the panel made it inert
   * on click for the same reason: opening it would suggest the project can edit a
   * workspace-level configuration, which it cannot. Its one action is to start a project
   * configuration from it.
   */
  handleRowClick = (config: IConfig): void => {
    if (isReadOnlyReference(this.props.scope, config)) return;
    this.props.onOpenConfig(config);
  };

  /**
   * What a configuration can actually SAY about itself depends on the scope.
   *
   * A workspace configuration is a settings template that project configurations inherit:
   * `KDomainConfiguration` carries no platform flags at all (the editor forces them true
   * only so the shared form bodies render), and `isSelected` — the configuration Run and
   * Publish use — is a project's choice. Both columns would be permanently empty there, so
   * the workspace list is its names, when they last changed, and its row actions — nothing it
   * cannot fill.
   *
   * The Scope column is also what tells the recent tab's rows apart: that tab mixes both stores,
   * and says which a row came from in the colours `CONFIG_SCOPE_COLOUR` gives them everywhere.
   */
  getColumns = (): KuikaListTableColumn<IConfig>[] => [
    ...this.getSurfaceColumns(),
    buildLastModifiedColumn<IConfig>(this.state.feed.index, (row: IConfig) => row.id)
  ];

  getSurfaceColumns = (): KuikaListTableColumn<IConfig>[] => {
    const columns: KuikaListTableColumn<IConfig>[] = [
      {
        id: "name",
        header: () => "Name",
        accessorFn: (row: IConfig) => row.name?.toLowerCase() ?? "",
        cell: (row: IConfig) => (
          <span
            className="kuika-config-list__name-cell"
            data-testid={configRowTestId(row)}
            style={
              {
                // The panel tinted a global row's glyph blue; the tint travels with it.
                "--kuika-list-table-icon-bg": isGlobalRow(row) ? "var(--blue-1)" : "var(--dark-2)",
                "--kuika-list-table-icon-color": isGlobalRow(row) ? "var(--blue-5)" : "var(--dark-7)"
              } as React.CSSProperties
            }
          >
            <KuikaListTableNameCell
              name={row.name}
              icon={<KuikaSvg type="topbar-icons" name="config-manager" width={14} height={14} />}
            />
          </span>
        )
      }
    ];

    if (!this.isProjectSurface()) {
      return columns;
    }

    columns.push({
      id: "platforms",
      header: () => "Platforms",
      width: 220,
      accessorFn: (row: IConfig) => countConfigPlatforms(row),
      cell: (row: IConfig) => {
        const platforms: IConfigPlatform[] = getConfigPlatforms(row);
        if (platforms.length === 0) {
          return <span className="kuika-list-table__muted">—</span>;
        }
        return (
          <span className="kuika-config-list__platforms">
            {platforms.map((platform: IConfigPlatform) => (
              <KuikaBadge
                key={platform.key}
                type="label"
                size="small"
                color={platform.color}
                content={platform.label}
              />
            ))}
          </span>
        );
      }
    });

    // Only the project's list mixes the two stores, so only it needs to say which is which.
    columns.push({
      id: "scope",
      header: () => "Scope",
      width: 120,
      accessorFn: (row: IConfig) => (isGlobalRow(row) ? 1 : 0),
      cell: (row: IConfig) =>
        isGlobalRow(row) ? (
          <KuikaListTableStatusCell
            label="Global"
            color={CONFIG_SCOPE_COLOUR.global}
            hint="A workspace configuration. Edit it in Global Configurations, or start a project configuration from it."
          />
        ) : (
          <KuikaListTableStatusCell label="Project" color={CONFIG_SCOPE_COLOUR.project} />
        )
    });

    columns.push({
      id: "status",
      header: () => "Status",
      width: 120,
      accessorFn: (row: IConfig) => (row.isSelected ? 0 : 1),
      cell: (row: IConfig) =>
        row.isSelected ? (
          <KuikaListTableStatusCell label="Selected" color="green" hint="The configuration Run and Publish use." />
        ) : null
    });

    return columns;
  };

  getRowActions = (): KuikaListTableRowAction<IConfig>[] =>
    buildConfigRowActions({
      scope: this.props.scope,
      onCreateFromGlobal: this.openCreateFromGlobalModal,
      onRename: this.props.onRenameConfig ? this.openRenameModal : undefined,
      onDuplicate: this.props.onDuplicateConfig,
      onRequestDelete: this.props.onRequestDelete
    });

  getEmptyTitle = (isSearchMiss: boolean, isEmptyRecents: boolean): string => {
    if (isSearchMiss) return "No configuration found";
    if (isEmptyRecents) return "Nothing changed recently";
    return "Configuration";
  };

  getEmptyDescription = (isSearchMiss: boolean, isEmptyRecents: boolean): string => {
    if (isSearchMiss) return "We couldn't find any items matching your search.";
    if (isEmptyRecents) {
      // A failed feed is said out loud rather than shown as "nothing happened", which would be a
      // claim the page cannot actually make.
      if (this.state.feed.status !== "failed") return "Configurations edited in the last 30 days appear here.";
      const fallbackLabel: string = this.isProjectSurface() ? "Project" : ALL_TAB_LABEL;
      return `We couldn't load recent activity — open ${fallbackLabel} to see every configuration.`;
    }
    return "No configurations have been added yet.";
  };

  /**
   * The list for one tab. It takes the tab rather than reading the active one, because the
   * leaving pane of a tab switch is drawn from here too — reading state, both panes would show
   * the newly chosen tab's rows and the switch would look like nothing happened.
   */
  renderTable = (paneKey: string): React.ReactNode => {
    const isRecentPane: boolean = paneKey === RECENTLY_MODIFIED_TAB;
    const rows: IConfig[] = this.getPaneRows(paneKey, this.state.searchText);
    const isSearching: boolean = this.state.searchText.trim().length > 0;
    // The panel's precedence: "nothing here yet" wins over the search miss. The recent tab asks it
    // of every configuration — a search that misses there is still a search miss.
    const unsearched: IConfig[] = isRecentPane ? this.getSurfaceConfigs("") : this.getPaneRows(paneKey, "");
    const isSearchMiss: boolean = unsearched.length > 0 && isSearching;
    // The recent tab is only reached with nothing in it when the user picked it deliberately, or
    // when a search emptied it — the page opens on the full list otherwise.
    const isEmptyRecents: boolean = isRecentPane && rows.length === 0 && !isSearching;
    // The page parks on the recent tab while the feed is in flight. Without this it would paint an
    // empty table first and claim nothing had changed recently — something it cannot yet know.
    const isFeedPending: boolean = isRecentPane && this.state.feed.status === "loading";
    const isLoading: boolean = this.props.isConfigListLoading === true || (isFeedPending && rows.length === 0);

    return (
      <KuikaListTable<IConfig>
        data={rows}
        loading={isLoading}
        columns={this.getColumns()}
        rowActions={this.getRowActions()}
        rowActionsTestId={(row: IConfig) => `${configTestName(row)}ThreeDotMenuButton`}
        rowKey={(row: IConfig) => row.id}
        // Only the tab on screen reveals; the pane leaving a tab switch is not the one to light.
        revealRowKey={paneKey === this.getActiveTab() ? this.findRevealRowId(rows) : null}
        onRowRevealed={this.handleRowRevealed}
        isRowBusy={(row: IConfig) => row.id === this.props.busyConfigId}
        onRowClick={this.handleRowClick}
        emptyIcon={
          <KuikaSvg
            name={isSearchMiss ? "search-off" : "config-manager"}
            type={isSearchMiss ? "common" : "topbar-icons"}
            width={32}
            height={32}
          />
        }
        emptyVariant="page"
        emptyTitle={this.getEmptyTitle(isSearchMiss, isEmptyRecents)}
        emptyDescription={this.getEmptyDescription(isSearchMiss, isEmptyRecents)}
        data-testid="configManagerTable"
      />
    );
  };

  render() {
    const isProject: boolean = this.isProjectSurface();
    const activeTab: string = this.getActiveTab();

    return (
      <>
        <KuikaListPage className="kuika-config-list" data-testid="configManagerScreen">
          <KuikaListPageHeader
            icon={<KuikaSvg name="config-manager" type="topbar-icons" width={20} height={20} />}
            title={isProject ? "Config Manager" : "Global Configurations"}
            titleTestId="configManagerTitle"
            actions={this.props.headerActions}
          />

          <KuikaCreateCardRow>
            <KuikaCreateCard
              title="Create a blank configuration"
              description="Set up an environment of your own — test, staging or production."
              icon={<KuikaSvg name="plus" width={20} height={20} />}
              iconBackground="var(--blue-1)"
              onClick={() => this.openCreateModal()}
              data-testid="configManagerCreateCard"
            />
          </KuikaCreateCardRow>

          <KuikaListPageToolbar
            tabs={
              <KuikaTabs
                size="lg"
                options={[
                  // `label` is not optional in practice: KuikaTabs renders `icon || label`, so an
                  // option carrying only a value draws an empty pill.
                  {
                    value: RECENTLY_MODIFIED_TAB,
                    label: RECENTLY_MODIFIED_TAB,
                    dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID
                  },
                  ...(isProject
                    ? [
                        { value: PROJECT_TAB, label: "Project", dataTestId: "configManagerProjectTab" },
                        { value: GLOBAL_TAB, label: "Global", dataTestId: "configManagerGlobalTab" }
                      ]
                    : [{ value: GLOBAL_TAB, label: ALL_TAB_LABEL, dataTestId: "configManagerAllTab" }])
                ]}
                value={activeTab}
                onChange={this.handleTabChange}
                dataTestId="configManagerTabs"
              />
            }
            search={
              <KuikaInput
                size="md"
                placeholder="Search..."
                value={this.state.searchText}
                leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                  this.handleSearchChange(String(value))
                }
                data-testid="searchInput"
                rightIcon={
                  this.state.searchText ? (
                    <button
                      type="button"
                      className="kuika-config-list__search-clear"
                      aria-label="Clear search"
                      data-testid="clearSearchInputButton"
                      onClick={() => this.handleSearchChange("")}
                    >
                      <KuikaSvg name="x" width={12} height={12} />
                    </button>
                  ) : undefined
                }
              />
            }
          />

          <KuikaTabContent
            // The tab the page lands on by itself is not a switch anyone made, so it must not slide
            // in the way one does — on most visits nothing is recent, and every one would open on a
            // slide. Remounting when the page stops choosing makes that pane the content's first,
            // which never animates; every switch after it slides as before.
            key={this.isChoosingTab() ? "choosing" : "chosen"}
            className="kuika-config-list__panes"
            activeKey={activeTab}
            order={isProject ? PROJECT_SURFACE_TAB_ORDER : GLOBAL_SURFACE_TAB_ORDER}
            render={this.renderTable}
          />
        </KuikaListPage>

        <ConfigNameDialog
          scope={this.props.scope}
          configs={this.props.configs ?? []}
          dialog={this.state.nameDialog}
          onClose={this.closeNameModal}
          onCreateConfig={this.props.onCreateConfig}
          onRenameConfig={this.props.onRenameConfig}
        />
      </>
    );
  }
}
